import { WarsGame } from "@playroomkit/pr-wars";
import { ShopItemType, StoreSkuItem } from "@playroomkit/pr-wars-data";
import { UrlMapper } from "@playroomkit/pr-common";
import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from "react";
import { useManager } from "./WarsContext";
import { useUrlMapper } from "./URLMapperContext";
import { useShop } from "./ShopProvider";
import {
  SendPlayerAllAccessStatusEvent,
  ShopDataEvent,
  WarsEvents,
} from "@playroomkit/pr-wars/events";

export interface Bot {
  id: string;
  skuId?: string;
  name: string;
  about: string;
  modelImageUrl: string;
  selected: boolean;
  availability: string;
  enabled: boolean;
  price?: string | number;
}

interface BotsContextValue {
  bots: Bot[];
  updateBots: (allAccess?: boolean, items?: StoreSkuItem[]) => void
  toggleBot: (id: string) => void;
}

const BotsContext = createContext<BotsContextValue | undefined>(undefined);

// Todo: Move all the logic to WarsGame
export const BotsProvider = ({ children }: { children: ReactNode }) => {
  const manager = useManager();
  const urlMapper = useUrlMapper();
  const { shopItems } = useShop();

  const [hasAllAccess, setHasAllAccess] = useState<boolean>(false);
  const [bots, setBots] = useState<Bot[]>(
    getAvailableBots(manager, shopItems, urlMapper)
  );

  useEffect(() => {
    const handleStateSyncEvent = (event: Event) => {
      const allAccessStatus = !!manager.getMyPlayer()?.allAccess;
      setHasAllAccess(allAccessStatus);

      updateBots(allAccessStatus, shopItems);
    };

    const handleShopDataEvent = (event: Event) => {
      const e = event as ShopDataEvent;
      const items = e.data;
      
      updateBots(hasAllAccess, items);
    };

    const handlePlayerAllAccessStatus = (event: Event) => {
      const e = event as SendPlayerAllAccessStatusEvent;
      setHasAllAccess(e.allAccess);

      updateBots(e.allAccess, shopItems);
    };

    document.addEventListener(WarsEvents.StateSyncEvent, handleStateSyncEvent);
    document.addEventListener(WarsEvents.ShopDataEvent, handleShopDataEvent);
    document.addEventListener(
      WarsEvents.SendPlayerAllAccessStatusEvent,
      handlePlayerAllAccessStatus
    );

    // Cleanup function to remove event listeners
    return () => {
      document.removeEventListener(
        WarsEvents.StateSyncEvent,
        handleStateSyncEvent
      );
      document.removeEventListener(WarsEvents.ShopDataEvent, handleShopDataEvent);
      document.removeEventListener(
        WarsEvents.SendPlayerAllAccessStatusEvent,
        handlePlayerAllAccessStatus
      );
    };
  }, [hasAllAccess, bots, shopItems]);

  const updateBots = (allAccess = hasAllAccess, items = shopItems) => {
    const bots = getAvailableBots(manager, items, urlMapper);

    if (allAccess) {
      enableAllBots(bots);
    } else {
      setBots(bots);
    }
  };

  const toggleBot = (id: string) => {
    const updatedBots = [...bots];

    const botIndex = updatedBots.findIndex((bot) => bot.id === id);

    if (botIndex > -1) {
      updatedBots[botIndex].selected = !updatedBots[botIndex].selected;
    }

    if (updatedBots[botIndex].selected) {
      manager.addBot(id);
    } else {
      manager.removeBot(id);
    }

    setBots(updatedBots);
  };

  const enableAllBots = (bots: Bot[]) => {
    const updatedBots = bots.map((bot) => {
      return {
        ...bot,
        enabled: true,
      };
    });

    setBots(updatedBots);
  };

  function getAvailableBots(
    manager: WarsGame,
    shopItems: StoreSkuItem[],
    urlMapper: UrlMapper
  ) {
    const players = manager.getPlayers();

    let bots = manager.getAvailableBots(),
      r: Bot[] = [];

    for (let i = 0; i < bots.length; ++i) {
      let bot = bots[i];
      const sku = shopItems.find(
        (item) =>
          item.metadata?.type === ShopItemType.BOT &&
          item.metadata?.id === bot.uid
      );

      r.push({
        id: bot.uid!,
        skuId: sku?.sku_id,
        name: bot.displayName!,
        about: bot.about!,
        modelImageUrl: bot.modelImageUrl ?? "",
        availability: bot.availability ?? "free",
        selected: !!players.find((player) => player.uid === bot.uid),
        enabled: bot.availability === "free" || !!sku?.purchased,
        ...(bot.availability === "paid" && { price: sku?.price }),
      });
    }

    return r.sort((a, b) => {
      if (a.availability === "free" && b.availability !== "free") {
        return -1;
      }
      if (a.availability !== "free" && b.availability === "free") {
        return 1;
      }
      return 0;
    });
  }

  return (
    <BotsContext.Provider value={{ bots, updateBots, toggleBot }}>
      {children}
    </BotsContext.Provider>
  );
};

export const useBots = () => {
  const context = useContext(BotsContext);
  if (!context) {
    throw new Error("useBots must be used within an BotsProvider");
  }
  return context;
};
